import { ValidationErrors } from '@angular/forms';

export type CreditCardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'discover'
  | 'diners'
  | 'jcb'
  | 'unionpay'
  | 'unknown';

export interface CreditCardTypeInfo {
  brand: CreditCardBrand;
  displayName: string;
  lengths: readonly number[];
  mask: string;
  cvvLength: number;
}

interface CardBrandDefinition {
  brand: CreditCardBrand;
  displayName: string;
  pattern: RegExp;
  lengths: readonly number[];
  mask: string;
  cvvLength: number;
}

const CARD_BRANDS: readonly CardBrandDefinition[] = [
  {
    brand: 'amex',
    displayName: 'American Express',
    pattern: /^3[47]/,
    lengths: [15],
    mask: '9999 999999 99999',
    cvvLength: 4,
  },
  {
    brand: 'diners',
    displayName: 'Diners Club',
    pattern: /^3(?:0[0-5]|[68])/,
    lengths: [14, 16, 17, 18, 19],
    mask: '9999 999999 9999',
    cvvLength: 3,
  },
  {
    brand: 'visa',
    displayName: 'Visa',
    pattern: /^4/,
    lengths: [13, 16, 19],
    mask: '9999 9999 9999 9999',
    cvvLength: 3,
  },
  {
    brand: 'mastercard',
    displayName: 'Mastercard',
    pattern: /^(?:5[1-5]|2[2-7])/,
    lengths: [16],
    mask: '9999 9999 9999 9999',
    cvvLength: 3,
  },
  {
    brand: 'discover',
    displayName: 'Discover',
    pattern: /^6(?:011|5|4[4-9])/,
    lengths: [16, 19],
    mask: '9999 9999 9999 9999',
    cvvLength: 3,
  },
  {
    brand: 'jcb',
    displayName: 'JCB',
    pattern: /^35(?:2[89]|[3-8])/,
    lengths: [16],
    mask: '9999 9999 9999 9999',
    cvvLength: 3,
  },
  {
    brand: 'unionpay',
    displayName: 'UnionPay',
    pattern: /^62/,
    lengths: [16, 17, 18, 19],
    mask: '9999 9999 9999 9999 999',
    cvvLength: 3,
  },
];

const DEFAULT_CARD_INFO: CreditCardTypeInfo = {
  brand: 'unknown',
  displayName: 'Card',
  lengths: [13, 14, 15, 16, 17, 18, 19],
  mask: '9999 9999 9999 9999 999',
  cvvLength: 3,
};

export function stripCardDigits(value: string | null | undefined): string {
  return String(value ?? '').replace(/\D/g, '');
}

export function detectCreditCardType(digits: string): CreditCardTypeInfo {
  for (const definition of CARD_BRANDS) {
    if (definition.pattern.test(digits)) {
      return {
        brand: definition.brand,
        displayName: definition.displayName,
        lengths: definition.lengths,
        mask: definition.mask,
        cvvLength: definition.cvvLength,
      };
    }
  }

  return { ...DEFAULT_CARD_INFO };
}

/** Luhn (mod 10) check for credit card numbers. */
export function luhnCheck(digits: string): boolean {
  const normalized = stripCardDigits(digits);
  if (!normalized) {
    return false;
  }

  let sum = 0;
  let shouldDouble = false;

  for (let i = normalized.length - 1; i >= 0; i--) {
    let digit = parseInt(normalized[i], 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
}

export function canGrowToValidLength(digits: string, info: CreditCardTypeInfo): boolean {
  return info.lengths.some((length) => length > digits.length);
}

export function isCompleteCardLength(digits: string, info: CreditCardTypeInfo): boolean {
  return info.lengths.includes(digits.length);
}

export function validateCreditCardNumber(value: string | null | undefined): ValidationErrors | null {
  const digits = stripCardDigits(value);
  if (!digits) {
    return null;
  }

  const info = detectCreditCardType(digits);
  const maxLength = Math.max(...info.lengths);

  if (digits.length > maxLength) {
    return { creditCardInvalid: { message: 'Invalid credit card number' } };
  }

  if (isCompleteCardLength(digits, info)) {
    return luhnCheck(digits) ? null : { creditCardInvalid: { message: 'Invalid credit card number' } };
  }

  return { creditCardIncomplete: { message: 'Enter a valid credit card number' } };
}

export function getCvvPattern(cvvLength: number): RegExp {
  return cvvLength === 4 ? /^\d{4}$/ : /^\d{3}$/;
}

export function getMaxCardDigits(info: CreditCardTypeInfo = DEFAULT_CARD_INFO): number {
  return Math.max(...info.lengths);
}

/** Formats digits with spaces according to the detected card mask (e.g. 4-4-4-4 or 4-6-5 for Amex). */
export function formatCardNumberDisplay(digits: string): string {
  const info = detectCreditCardType(digits);
  const maxLength = getMaxCardDigits(info);
  const value = stripCardDigits(digits).slice(0, maxLength);
  const groupSizes = info.mask.split(' ').map((segment) => segment.length);

  const groups: string[] = [];
  let position = 0;

  for (const size of groupSizes) {
    if (position >= value.length) {
      break;
    }
    groups.push(value.slice(position, position + size));
    position += size;
  }

  return groups.join(' ');
}

export function normalizeCardDigits(value: string | null | undefined): string {
  const digits = stripCardDigits(value);
  if (!digits) {
    return '';
  }

  const info = detectCreditCardType(digits);
  return digits.slice(0, getMaxCardDigits(info));
}
